import { QueryKeys } from './services.types';

/**
 * This function is used to generate query keys for useQuery based on the path provided.
 * The path is split by '/' and the resulting array is used as the main key.
 * So for example, if the path is '/projects/1/microgrids/1', the main key will be ['projects', '1', 'microgrids', '1'].
 * The list and detail keys are generated by appending 'list' and 'detail' to the main key.
 * @param {string} path
 * @returns object with mainKey, list and detail keys
 */
export const getQueryKeys = (path: string): QueryKeys => {
  const mainKey = path.split('/').filter((item) => item);
  return {
    mainKey,
    list: (filters) => [...mainKey, 'list', { filters }],
    detail: [...mainKey, 'detail'],
  };
};

export const USER_ME_CACHE_KEY = ['users/me'];
export const USERS_CACHE_KEY = (
  page: string = '',
  perPage: string = '',
  organisationId: string = '',
  searchTerm: string = '',
  order: string = ''
) => ['users', page, perPage, organisationId, searchTerm, order];
export const USER_CACHE_KEY = (userId: string) => ['user', userId];
export const ASSET_CACHE_KEY = (assetId: string) => ['asset', assetId];
export const DEVICE_MODEL_CACHE_KEY = (deviceModelId: string) => ['deviceModel', deviceModelId];
export const ORGANISATION_CACHE_KEY = (organisationId: string = '') => ['organisation', organisationId];
export const ORGANISATIONS_CACHE_KEY = (
  perPage: number = 12,
  searchTerm: string = '',
  filters: { key: string; value: string | number | boolean }[] = []
) => ['organisations', perPage.toString(), searchTerm, JSON.stringify(filters)];
export const ASSETS_CACHE_KEY = (searchTerm: string = '', organisationId: string = '') => [
  'assets',
  searchTerm,
  organisationId,
];
export const RECENT_ASSETS_CACHE_KEY = ['assets', 'recent'];
export const PROJECTS_CACHE_KEY = (searchTerm: string = '', organisationId: string = '') => [
  'all-projects',
  organisationId,
  searchTerm,
];
export const PROJECT_CACHE_KEY = (externalId: string) => ['project', externalId];
export const FAVOURITE_PROJECTS_CACHE_KEY = ['all-projects', 'favourites']

export const PROJECT_TYPES_CACHE_KEY = ['project-types'];

export const BLUEPRINTS_BASE_KEY = ['blueprints'] as const;

export const BLUEPRINT_DETAILS_CACHE_KEY = (blueprintId: string | number) => [
  'blueprint-details',
  blueprintId.toString(),
];

export const BLUEPRINT_POWER_PROFILE_CACHE_KEY = (blueprintId: string | number) => [
  'blueprint-power-profile',
  blueprintId.toString(),
];

export const BLUEPRINT_SHARED_TOKEN_CACHE_KEY = (blueprintId: string | number) => [
  'blueprint-shared-token',
  blueprintId.toString(),
];

export const EQUIPMENT_CACHE_KEY = (organisationId: string) => ['equipment', 'list', organisationId];
export const EQUIPMENT_DETAIL_CACHE_KEY = (equipmentId: string) => ['equipment', 'detail', equipmentId];
export const RENTAL_BATTERIES_CACHE_KEY = ['equipment', 'rental-batteries'];
export const RENTAL_BATTERY_DETAIL_CACHE_KEY = (batteryId: string) => ['equipment', 'rental-battery-detail', batteryId];

export const EVENTS_CACHE_KEY = ({
  unitId,
  perPage = 50,
  severityFilters = [],
  filters = [],
}: {
  unitId: string;
  perPage?: number;
  severityFilters?: string[];
  filters?: { filterKey: string; value: string | number | Array<string | number> }[];
}) => [
  'events',
  unitId,
  perPage.toString(),
  ...severityFilters,
  ...filters.map((f) => (Array.isArray(f.value) ? `${f.filterKey}:${f.value.join(',')}` : `${f.filterKey}:${f.value}`)),
];

export const FLEETS_CACHE_KEY = ['fleets'];
export const FLEET_CACHE_KEY = (fleetId: string | number = '') => ['fleet', `${fleetId}`];

export const REPORTS_CACHE_KEY = (externalId: string = '') => [`${externalId}`, 'reports'];
export const USER_ROLES_CACHE_KEY = ['user-roles'];
export const ALERT_ROBOCALL_RECIPIENTS_CACHE_KEY = (organisationId: number) => ['alert-robocalls', `${organisationId}`];
export const ALERT_EMAIL_RECIPIENTS_CACHE_KEY = (organisationId: number) => ['alert-emails', `${organisationId}`];
export const ALERT_EVENT_CONFIGS_CACHE_KEY = ['alert-event-configs'];
export const ALERT_EVENT_ENABLE_FLAGS_CACHE_KEY = (organisationId: string | number) => [
  'alert-event-enable-flags',
  `${organisationId}`,
];

export const COUNTRIES_CACHE_KEY = ['countries'];
export const DEVICE_TYPES_CACHE_KEY = ['device-types'];
export const WATCHER_TYPE_CACHE_KEY = (watcherTypeId: number | string) => [`${watcherTypeId}`, 'watcher-type'];
export const WATCHER_TYPES_CACHE_KEY = (perPage = 10, searchTerm = '', order = 'asc') => [
  'watcher-types',
  `${perPage}`,
  `${searchTerm}`,
  `${order}`,
];
